<!-- 
  Krpano.svelte

  Description:
  This component renders the krpano viewer and handles passing configuration to the krpano viewer.

  Props:
    - config: Object - tour-config.json object from the tour
    - xml: String - tour.xml file path or xml content
    - logoLoading: String - Path to the loading logo
    - logoLoadingWidth: Number - Width of the loading logo
    - colors: Object - Colors / Theme object from the tour-config.json
    - sceneIds: Array - Optional array of scene IDs to filter which scenes to show
 -->
<script>
  import { trackSceneViewStart, trackHotspotClick } from "./analytics.js";
  import { createEmbedXMLUrl } from "./xml.js";
  import { recolorHotspotSvg, clearSvgCaches } from "./svgRecolor.js";

  /* ____________ Mobile Detection ____________ */
  import UA from "ua-parser-js";
  const ua = new UA();
  let isMobile = ua.getResult().device["type"] == "mobile";
  
  const key = Math.random().toString();
  export let config;
  export let xml = null; // Make xml optional
  export let logoLoading = "./images/logo.png";
  export let logoLoadingWidth = 500;
  export let colors;
  export let loaderEnabled = true;
  export let expanded;
  export let enableTracking = true;
  export let sceneIds = null; // Optional array of scene IDs to filter
  export let onHotspotHashLoaded = null; // Callback function when hotspot hash should be processed
  const tourConfig = config;

  import { onMount, onDestroy } from "svelte";
  import Loader from "./Loader.svelte";
  import { tourFunctions } from "../tour-functions.js";

  let krpano;
  let krpanoLoading = true;
  let interval; // interval for checking if krpano is ready
  let xmlUrl; // Store the generated XML URL
  let isFirstLoadedScene = true;
  let hasLittlePlanet = false;
  let pendingStartLittlePlanet = false;
  let littlePlanetIntroStarted = false;

  const LOADER_FADE_MS = 2000;
  const prefersReducedMotion =
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  let panoRevealed = !loaderEnabled || prefersReducedMotion;

  const isRouteView = Boolean(tourConfig?.salesRouteId || tourConfig?.routeId);
  const hasIntroPopup = !isRouteView && Boolean(
    tourConfig?.features?.includes("introPopup") &&
      tourConfig?.introPopup?.enabled &&
      (tourConfig?.introPopup?.heading ||
        tourConfig?.introPopup?.logoUrl ||
        tourConfig?.introPopup?.contentHtml ||
        tourConfig?.introPopup?.videoEmbedUrl ||
        (tourConfig?.introPopup?.showRoutes &&
          tourConfig?.features?.includes("routes") &&
          (tourConfig?.routes?.filter((r) => r.featured)?.length ?? 0) > 0))
  );

  // Get the hotspot hash from the url
  const hotspotHash = window.location.hash.match(/^#\/hotspot\/(.+)$/);
  const hotspotId = hotspotHash ? hotspotHash[1] : null;
  

  
  window.CC = window.CC || {}; // using window is kind of hacky, but it works for now
  // make trackHotspotClick available to krpano
  window.CC.trackHotspotClick = trackHotspotClick;
  // make recolorHotspotSvg available to krpano
  window.CC.recolorHotspotSvg = (hotspotName, srcUrl, color) => recolorHotspotSvg(hotspotName, srcUrl, color, krpano);



  const queryStrings = tourFunctions.getQueryStrings();
  const startSceneOptions = { hotspotId, queryStrings };
  const startSceneIndex = tourFunctions.getStartSceneIndex(tourConfig, startSceneOptions);
  const startSceneName = tourFunctions.getStartSceneName(tourConfig, startSceneOptions);

  function onNewPanoLegacyTrackScene(scene) {
    if (!enableTracking) return;
    const sceneTitle = scene.title.replace('[br]',' ');
    if(window.gtag){ 
      gtag('event', 'view', {
        'event_category': 'Scenes',
        'event_label':  sceneTitle
      });
    }
  }

  function onNewPanoTrackScene(scene) {
    if (!enableTracking) return;
    const scene_id = scene.id;
    const scene_title = krpano.get("scene[get(xml.scene)].title") || scene_id;
    const scene_source = window.CC.pendingSceneSource || 'krpano';
    trackSceneViewStart({
      scene_id,
      scene_title,
      scene_source,
    });
    // Clear the pending source after using it
    window.CC.pendingSceneSource = null;
  }

  function onReadyKrpano(krpanoHandle) {
    krpano = krpanoHandle;
    window.CC = window.CC || {}; // using window is kind of hacky, but it works for now
    window.CC.krpano = krpano;

    if (pendingStartLittlePlanet && !littlePlanetIntroStarted) {
      littlePlanetIntroStarted = true;
      pendingStartLittlePlanet = false;
      krpano?.call("startLittlePlanetIntro();");
    }

    // When there's no loader (e.g. map-first minimized preview), nothing calls the loader-done
    // hook, so release the autorotate gate as soon as krpano is ready.
    if (!loaderEnabled) {
      krpano?.call("enable_autorotate_after_load();");
    }

    // Setup tour for minimized view
    if (!expanded) {
      // Hide hotspots
      tourFunctions.hideHotspots();
    }

    // Track scene changes
    krpano.events.addListener("onnewpano", () => {
      // Call the onHotspotHashLoaded callback if provided
      if (onHotspotHashLoaded && typeof onHotspotHashLoaded === 'function' && hotspotId) {
        onHotspotHashLoaded({ hotspotId });
      }

      const scene = krpano.get("scene[get(xml.scene)]");
      onNewPanoTrackScene(scene);
      onNewPanoLegacyTrackScene(scene);
      // if showing tour (expanded) and not on first loaded scene, set the stop url
      if (expanded && !isFirstLoadedScene) tourFunctions.setStopUrl(scene);
      // flag the first scene has been loaded
      isFirstLoadedScene = false;
    });
  }

  function startKrpano() {
    // convert 0x000000 to #000000
    const hexHotspotTextColor = colors?.hotspotTextColor
      ? colors.hotspotTextColor.replace("0x", "#")
      : "#000000";

    // separate callout color into color and alpha for krpano
    const calloutColor = colors?.calloutColor || "0x000000";
    const calloutColorOnly = calloutColor.slice(0, 8);
    const calloutColorAlpha =
      calloutColor.length > 8
        ? parseInt(calloutColor.slice(8), 16) / 255.0
        : 1.0;
    // Accessiblility: Prefers Reduced Motion
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    const openAda = queryStrings.ada;
    // Little Planet (Enable/Disable)
    hasLittlePlanet = tourConfig?.hasLittlePlanet ?? true;
    const hasRemoveIntroAnimation = tourConfig?.features?.includes('removeIntroAnimation');
    if (hotspotId || prefersReducedMotion || openAda || hasRemoveIntroAnimation || !expanded) hasLittlePlanet = false;
    // Autorotate (Enable/Disable)
    let hasAutorotate = true;
    if (prefersReducedMotion) hasAutorotate = false;
    if (openAda) hasAutorotate = false;
    const autoRotateSpeed = tourConfig.autoRotateSpeed ?? 3;
    // Hotspot Animations
    const hasHotspotAnimations = tourConfig.hasHotspotAnimations ?? true;
    // Info
    const hotspotInfoUrl =
      tourConfig.hotspotInfo?.url ?? "%SWFPATH%/images/info.svg";
    const hotspotInfoWidth = tourConfig.hotspotInfo?.width ?? 25;
    const hotspotInfoHeight = tourConfig.hotspotInfo?.height ?? 25;
    const hotspotInfoCustomWidth = tourConfig.hotspotInfoCustom?.width ?? 28;
    const hotspotInfoCustomHeight = tourConfig.hotspotInfoCustom?.height ?? 28;
    // Gallery
    const hotspotGalleryUrl =
      tourConfig.hotspotGallery?.url ?? "%SWFPATH%/images/gallery.svg";
    const hotspotGalleryWidth = tourConfig.hotspotGallery?.width ?? 40;
    const hotspotGalleryHeight = tourConfig.hotspotGallery?.height ?? 40;
    // Video
    const hotspotVideoUrl =
      tourConfig.hotspotVideo?.url ?? "%SWFPATH%/images/video.svg";
    const hotspotVideoWidth = tourConfig.hotspotVideo?.width ?? 40;
    const hotspotVideoHeight = tourConfig.hotspotVideo?.height ?? 40;
    // Social
    const hotspotSocialUrl =
      tourConfig.hotspotSocial?.url ?? "%SWFPATH%/images/social-instagram-reel.svg";
    const defaultSocialSize = isMobile ? 40 : 45;
    const hotspotSocialWidth = tourConfig.hotspotSocial?.width ?? defaultSocialSize;
    const hotspotSocialHeight = tourConfig.hotspotSocial?.height ?? defaultSocialSize;
    const hotspotLocationSizeDesktop = tourConfig.hotspotLocationSize ?? 50;
    const hotspotLocationSizeMobile = tourConfig.hotspotLocationSizeMobile ?? 45;
    const hotspotLocationBubbleSize = isMobile ? hotspotLocationSizeMobile : hotspotLocationSizeDesktop;
    const hotspotLocationBalloonSize = isMobile ? hotspotLocationSizeMobile : hotspotLocationSizeDesktop;
    // Inline Photo Gallery
    const hotspotInlinePhotoGallerySize =
      tourConfig.hotspotInlinePhotoGallerySize ?? 100;
    // Callout Distance Popup
    const calloutDistancePopup = tourConfig.calloutDistancePopup ?? 4;

    // Use startSceneIndex computed from URL (startscene, location, hotspot hash, stop hash)
    const startIndex = startSceneIndex;

    if (typeof embedpano === "function") {
      // Generate XML if not provided
      if (!xml) {
        // Filter scenes if sceneIds is provided (either from props or query params)
        const filteredScenes = sceneIds
          ? sceneIds
              .map((sceneId) =>
                tourConfig.scenes.find((scene) => scene.id === sceneId),
              )
              .filter(Boolean)
          : tourConfig.scenes;

        // If no scenes match the filter, use all scenes
        if (filteredScenes.length === 0) {
          console.warn('No scenes match the provided scene IDs. Showing all scenes.');
          xmlUrl = createEmbedXMLUrl(tourConfig.scenes);
        } else {
          xmlUrl = createEmbedXMLUrl(filteredScenes);
        }
        xml = xmlUrl;
      }

      embedpano({
        xml: xml,
        target: "pano-" + key,
        html5: "auto",
        webgl: "auto",
        passQueryParameters: false, // prevent xss (we must handle query strings manually)
        onready: onReadyKrpano,
        // Full-page tour needs keyboard focus (Enter / wheel). Minimized map preview should not steal it.
        focus: expanded,
        initvars: {
          expanded,
          startIndex,
          ...(startSceneName ? { startscene: startSceneName } : {}),
          ...(queryStrings.location != null ? { location: queryStrings.location } : {}),
          ...(queryStrings.ada != null ? { ada: queryStrings.ada } : {}),
          prefersReducedMotion,
          hotspotHoverColor: colors?.hotspotHoverColor || "0x000000",
          hexHotspotTextColor,
          calloutColor: calloutColorOnly,
          calloutColorAlpha,
          hasLittlePlanet,
          hasIntroPopup,
          hasHotspotAnimations,
          // Autorotate
          hasAutorotate,
          autoRotateSpeed,
          // AutoNav
          autoNavInterval: 10,
          // Info
          hotspotInfoUrl,
          hotspotInfoWidth,
          hotspotInfoHeight,
          hotspotInfoCustomWidth,
          hotspotInfoCustomHeight,
          // Gallery
          hotspotGalleryUrl,
          hotspotGalleryWidth,
          hotspotGalleryHeight,
          // Video
          hotspotVideoUrl,
          hotspotVideoWidth,
          hotspotVideoHeight,
          // Social
          hotspotSocialUrl,
          hotspotSocialWidth,
          hotspotSocialHeight,
          // Location
          hotspotLocationBubbleSize,
          hotspotLocationBalloonSize,
          // Inline Photo Gallery
          hotspotInlinePhotoGallerySize,
          // Callout Distance Popup
          calloutDistancePopup,
        },
      });
      const krpanoEl = document.getElementById("krpanoSWFObject");
      if (krpanoEl) {
        // set tabindex to 0 to allow focus #krpanoSWFObject
        krpanoEl.setAttribute("tabindex", "0");
        // label the pano for screen readers
        krpanoEl.setAttribute("aria-label", "Pano");
        // give application role
        krpanoEl.setAttribute("role", "application");
        syncKrpanoReveal();
      }

      krpanoLoading = false;
      return true;
    } else {
      return false;
    }
  }

  function startLittlePlanetNow() {
    if (!hasLittlePlanet || littlePlanetIntroStarted) return;
    const k = window.CC?.krpano ?? krpano;
    if (k) {
      littlePlanetIntroStarted = true;
      k.call("startLittlePlanetIntro();");
    } else {
      pendingStartLittlePlanet = true;
    }
  }

  function syncKrpanoReveal() {
    if (!panoRevealed) return;
    const el = document.getElementById("krpanoSWFObject");
    if (!el) return;
    const fadeMs = prefersReducedMotion ? 0 : LOADER_FADE_MS;
    el.style.setProperty("--pano-fade-ms", `${fadeMs}ms`);
    el.classList.toggle("pano-revealed", panoRevealed);
  }

  $: panoRevealed, syncKrpanoReveal();

  function handleLoaderDone() {
    panoRevealed = true;
    window.dispatchEvent(new CustomEvent("krpano:loaderdone"));
    startLittlePlanetNow();
    // Autorotate is held off during loading so it can't drift the start view. Now that the pano
    // is revealed, release the gate. It's a no-op while a little-planet intro is still running
    // (that path enables autorotate itself once the intro tween finishes).
    (window.CC?.krpano ?? krpano)?.call("enable_autorotate_after_load();");
    // Re-assert focus after reveal — embed-time focus can be lost to loader/UI chrome.
    if (expanded) {
      requestAnimationFrame(() => tourFunctions.focus());
    }
  }

    // Cleanup krpano
    function destroyKrpano() {
      if (krpano && typeof krpano.unload === "function") {
        krpano.unload();
        krpano = null;
      }
      const panoContainer = document.getElementById("pano-" + key);
      if (panoContainer) {
        panoContainer.innerHTML = "";
      }
    }

    // onMount lifecycle
    onMount(async () => {
      interval = setInterval(() => {
        if (startKrpano()) {
          console.log("Krpano started successfully");
          clearInterval(interval);
        }
      }, 1000);
    });

  // onDestroy lifecycle for cleanup
  onDestroy(() => {
    clearInterval(interval);
    destroyKrpano();    
    // Cleanup generated XML URL
    if (xmlUrl) {
      URL.revokeObjectURL(xmlUrl);
    }
  });

</script>

<!-- /*____________ Markup ____________*/ -->
<svelte:head>
  <!-- krpano viewer -->
  <script src={"tour.js"}></script>
</svelte:head>

{#if loaderEnabled}
<Loader
  logo={logoLoading}
  loading={krpanoLoading}
  delay="1000"
  width={logoLoadingWidth}
  tour={tourConfig}
  startSceneIndex={startSceneIndex}
  callback={handleLoaderDone}
/>
{/if}

<!-- pano container targeted by krpano -->
<div id={"pano-" + key} class="tour" key={key}></div>

<!-- /*____________ CSS ____________*/ -->
<style>
  div.tour {
    width: 100%;
    height: 100%;
    margin: 0;
    padding: 0;
    box-sizing: border-box;
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    background: var(--loader-bg, #000000);
  }

  :global(#krpanoSWFObject) {
    opacity: 0;
    transition: opacity var(--pano-fade-ms, 1000ms) ease;
  }

  :global(#krpanoSWFObject.pano-revealed) {
    opacity: 1;
  }
</style>
